import * as React from 'react';
import { claimVoipMasterIfVacant, isVoipEnabled, userSession } from 'state/session';
import { myCameraStream } from 'state/video';
import { user } from 'utils/auth/user';
import logger from 'utils/log';
import { tenantConfig } from 'utils/tenant';
import { voipConnectionEpoch, voipRtcConnection } from 'utils/ui/rtc';
import {
  dealerPseudonymOf,
  dedupeRemotesByDealer,
  filterEmptyDealerTiles,
  filterIdentityPendingTiles,
  resolveActiveConnectionId,
} from 'utils/ui/voipRemotes';
import type { RTCClient } from 'webrtc';
import type { TRTCConnection } from 'webrtc/types';

/**
 * Active-speaker ranking. Centralised so consumers (component layer, the hook
 * itself, and any future ranking-aware feature) share a single source of truth.
 * Returns 0 for speakers that should sort first, 1 otherwise.
 */
const speakerRank = (remote: TRTCConnection): number => (remote.isActiveSpeaker || remote.lastActiveSpeaker ? 0 : 1);

const useVoipConnections = () => {
  userSession().useState();
  tenantConfig().useState();
  myCameraStream().useState();
  // AVS-4302: re-render (and re-read the module binding below) whenever the connection is
  // rebuilt — a takeover/step-down swaps `voipRtcConnection` for a fresh RTCClient while the
  // VoipPanel stays mounted, so without this trigger the `store.useState()` call keeps its
  // subscription to the OLD store and the stepped-down master never sees the new master's
  // loopback. Reading the epoch first guarantees the line below resolves the CURRENT instance.
  voipConnectionEpoch.useState();

  const { remoteConnections: rawRemoteConnections } = voipRtcConnection?.store?.useState() || {
    remoteConnections: {},
  };

  // AVS-4302 (§5.5, CG-6): a standby dealer has no local camera; its own face arrives as
  // a REMOTE whose meta.dealerPseudonym matches this dealer (the master's mirror). That
  // loopback must NOT render as a separate participant tile, must NOT become the active
  // speaker, and must NOT be picked up by SingularVoip / onActiveParticipantChange — so
  // exclude it at the SOURCE (not just from sortedRemotes). It is surfaced separately as
  // `selfMirrorRemote` for the "You" tile. Identity-scoped + standby-gated so a flag-off
  // tab, a master, or a different dealer never filters anything (today's behaviour).
  const isStandby = userSession().isVoipStandby;
  const myPseudonym = user.userPseudonym;
  // AVS-4302: with 2+ standby tabs of the same dealer there are SEVERAL same-dealer remotes
  // (the publishing master PLUS the other receive-only standbys). The self-mirror must be the
  // one that actually carries the dealer's face — the PUBLISHING master, i.e. the remote with a
  // live stream — not just the first by arrival order. Picking first-by-order is non-deterministic
  // and, after a takeover or with 3+ tabs, can latch onto a streamless sibling standby, leaving the
  // "You" tile on a placeholder while the master's loopback is actually flowing. Prefer a
  // live-stream same-dealer remote; fall back to the first only until one starts streaming.
  const sameDealerRemotes = myPseudonym
    ? Object.values(rawRemoteConnections).filter((remote) => dealerPseudonymOf(remote) === myPseudonym)
    : [];
  const selfMirrorConnectionId = isStandby
    ? (sameDealerRemotes.find((remote) => remote.stream instanceof MediaStream) ?? sameDealerRemotes[0])?.connectionId
    : undefined;
  // Memoize so the standby's "You" tile keeps a stable ref across WebRTC store ticks —
  // otherwise the inner <Video> remounts on every tick (audio glitch risk).
  const selfMirrorRemote = React.useMemo(
    () => (selfMirrorConnectionId ? rawRemoteConnections[selfMirrorConnectionId] : undefined),
    [rawRemoteConnections, selfMirrorConnectionId]
  );

  // The single-stream flag gates BOTH the dedup (§5.6) and the empty-tile filter (§5.8).
  // It already requires remote + voip + voipSingleStream, so flag-off ⇒ the list is
  // untouched (exactly today's behaviour — the minimal-regression guarantee, and the
  // CG-4 design: a flag-ON viewer dedups even a stale/flag-off duplicate publisher).
  const isSingleStream = userSession().isVoipSingleStreamEnabled;
  // PO decision (AVS-4302): a suppressed standby shows ONLY its own Takeover tile — no
  // participant tiles at all. Render-only, like dedup: the gate below still keys on
  // rawPublishableCount, so the receive-only handshake and the master's loopback are untouched.
  const suppressIncoming = userSession().isVoipIncomingMediaSuppressed;

  // AVS-4302 Task 10: the render source, derived AFTER the Task 8 self-mirror exclusion.
  // When the flag is on we (1) drop the standby's own loopback (above), then (2) dedup
  // remotes by dealerPseudonym so the customer never sees one dealer twice, then (3) drop
  // empty dealer tiles (a same-dealer standby with no stream — e.g. the master's view of
  // its own receive-only standby). Dedup's tie-break keeps the active speaker, so the
  // active-speaker resolution still operates on a sane list.
  //
  // `rawPublishableCount` is the RAW peer count (self-mirror excluded, BUT before dedup &
  // the empty-tile filter). The publish/offer trigger keys on THIS — not the filtered
  // render count — because dedup/filter affect RENDERING only. A customer whose only peer
  // is a not-yet-streaming dealer (master connected before its stream arrived) would be
  // dropped from the render list, but it must STILL send its own publish() offer.
  const { remoteConnections, rawPublishableCount } = React.useMemo(() => {
    const withoutSelfMirror = selfMirrorConnectionId
      ? Object.fromEntries(Object.entries(rawRemoteConnections).filter(([id]) => id !== selfMirrorConnectionId))
      : rawRemoteConnections;
    const rawCount = Object.keys(withoutSelfMirror).length;
    if (suppressIncoming) {
      return { remoteConnections: {}, rawPublishableCount: rawCount };
    }
    // AVS-4302 takeover reliability: offer-admitted peers awaiting identity (metaPending) are
    // negotiable but NOT renderable — without meta the filters below read them as customers
    // (phantom tile). Render-only and unconditional (both flag states); rawCount above keeps
    // them, so the publish/negotiate gate is unaffected.
    const identified = Object.fromEntries(
      filterIdentityPendingTiles(Object.values(withoutSelfMirror)).map((remote) => [remote.connectionId, remote])
    );
    if (!isSingleStream) {
      return { remoteConnections: identified, rawPublishableCount: rawCount };
    }
    // AVS-4302: a dealer's OWN other tabs (same pseudonym) are never participant tiles. A
    // standby's own tab is the self-mirror — already excluded above and shown in the self tile;
    // a master's same-dealer remotes are its stepping-down predecessor or receive-only standby
    // siblings. `filterEmptyDealerTiles` only drops the STREAMLESS ones, so during a takeover the
    // OLD master's still-live stream would briefly render as a second participant tile next to the
    // new master's own camera (the reported takeover "double video"). Drop ALL same-dealer remotes
    // from the render list so a dealer never sees itself as a participant. Customers (no pseudonym)
    // are unaffected — `myPseudonym` is empty and never matches a dealer remote — so customer-side
    // dedup of DISTINCT dealers is unchanged. The publish/negotiate gate is unaffected: it keys on
    // `rawPublishableCount` (computed above, before this filter), so the new master still publishes
    // to the stepping-down tab and the old master still gets the new loopback.
    const renderableRemotes = myPseudonym
      ? Object.values(identified).filter((remote) => dealerPseudonymOf(remote) !== myPseudonym)
      : Object.values(identified);
    const display = filterEmptyDealerTiles(dedupeRemotesByDealer(renderableRemotes));
    return {
      remoteConnections: Object.fromEntries(display.map((conn) => [conn.connectionId, conn])),
      rawPublishableCount: rawCount,
    };
  }, [rawRemoteConnections, selfMirrorConnectionId, isSingleStream, suppressIncoming, myPseudonym]);

  const connections = Object.values(remoteConnections);
  const [activeConnectionId, setActiveConnectionId] = React.useState<string>('');

  // AVS-4302: `publish()` re-offers to every peer on each call, so it must fire at most
  // once per RTCClient instance. We track the instance we last published on (NOT a plain
  // boolean) so a takeover/step-down — which destroy()s and rebuilds the client — gets a
  // FRESH instance and therefore re-publishes. A boolean ref would stay `true` across the
  // rebuild and a tab that becomes master again in the same page lifetime would never
  // re-publish, leaving the customer without a dealer stream.
  const publishedConnectionRef = React.useRef<RTCClient | null>(null);
  // AVS-4302: a standby publishes no media but MUST still run one receive-only negotiation
  // pass per client instance (so it sends the offers it owes as lexical initiator and the
  // master's loopback actually arrives). Tracked on a SEPARATE ref from publishing so a
  // standby later promoted to master on the SAME instance still publishes its media — its
  // `publishedConnectionRef` is left untouched by the standby pass.
  const negotiatedConnectionRef = React.useRef<RTCClient | null>(null);

  // AVS-4302: depend on this tab's master status, not just connection count. A tab that
  // becomes master AFTER connections settle (fallback-claim timing, or a standby promoted
  // to master) must re-evaluate the publish gate — otherwise it never publishes and the
  // customer is left without a dealer stream.
  const myVoipMasterTabId = userSession().get('voipMasters')?.[user.userPseudonym]?.tabId;

  // AVS-4302: the standby's self-mirror (the master) is EXCLUDED from rawPublishableCount, but
  // it is precisely the peer the standby must run its receive-only handshake with to receive the
  // loopback. So a standby whose ONLY peer is the master — the customer has left (but
  // customerAllowed keeps VOIP active) or it connected before any customer — had a gate count of
  // 0 and NEVER negotiated: the master↔standby pair stayed at signalingState 'stable' and the
  // "You" tile showed a placeholder forever (the reported bug; confirmed on pr1623 with a stuck
  // pc at conn/ice 'new', sig 'stable'). Count the self-mirror back in for the gate. Publishing
  // is unaffected — a publisher is never a standby, so its self-mirror id is always undefined and
  // this equals rawPublishableCount (a publisher still needs a real, non-self peer).
  const peerCountForGate = rawPublishableCount + (selfMirrorConnectionId ? 1 : 0);

  React.useEffect(() => {
    // Gate on the RAW peer count, NOT the deduped/filtered render count (§5.6/§5.8 affect
    // rendering only). A customer whose sole peer is a not-yet-streaming dealer is dropped
    // from the render list but must still publish its own offer. The standby branch additionally
    // counts its self-mirror master (see peerCountForGate) so it negotiates even as its sole peer.
    if (peerCountForGate <= 0 || !voipRtcConnection) return;

    // A dealer that reaches the gate with a vacant master slot claims it (no-op when the
    // flag is off, when not a remote dealer, or when already claimed — `claimVoipMasterIfVacant`
    // early-returns on a taken slot). Then it EITHER publishes (master/customer) OR negotiates
    // receive-only (standby). The decision lives in state (`shouldPublishVoip`); the hook stays
    // a thin trigger. Each pass fires at most once per RTCClient instance (its own ref) so a
    // takeover/step-down rebuild re-runs the right one on the fresh instance.
    claimVoipMasterIfVacant();
    if (userSession().shouldPublishVoip) {
      if (publishedConnectionRef.current !== voipRtcConnection) {
        logger.log('[VOIP] SingularVOIP - Publishing user stream...');
        publishedConnectionRef.current = voipRtcConnection;
        // Cross-reset so a later same-instance demotion re-runs the receive-only pass.
        negotiatedConnectionRef.current = null;
        voipRtcConnection.publish();
      }
    } else if (negotiatedConnectionRef.current !== voipRtcConnection) {
      // Standby: no media, but it must negotiate (recvonly) so the master's loopback reaches
      // it. Without this, a peer pair where the standby owns the higher connectionId never
      // negotiates and the standby shows a placeholder until an unrelated mute/unmute on the
      // master forces an out-of-band offer.
      logger.log('[VOIP] SingularVOIP - Standby negotiating receive-only...');
      negotiatedConnectionRef.current = voipRtcConnection;
      // Cross-reset so a same-instance re-promotion re-publishes — a stale ref here would
      // strand receiveOnly=true and keep every incoming track disabled (permanent blackout).
      publishedConnectionRef.current = null;
      voipRtcConnection.negotiateAsReceiver();
    }
    // peerCountForGate already subsumes rawPublishableCount (it is that plus the self-mirror),
    // so it is the only count dep needed.
  }, [peerCountForGate, myVoipMasterTabId]);

  // AVS-4311: promote the last-known speaker to the single pill/active slot deterministically.
  // The selection rules live in `resolveActiveConnectionId` (utils, unit-tested) so this hook
  // stays a thin trigger. This also FIXES the old inline pick, which: fell back to the arbitrary
  // `connections[0]` (the "shows 1 person randomly" bug), clung to a departed `prev` (stale tile),
  // and via `find(isActiveSpeaker || lastActiveSpeaker)` could promote a stale last-speaker sitting
  // ahead of the live speaker (the helper now prefers `isActiveSpeaker` over `lastActiveSpeaker`).
  React.useEffect(() => {
    setActiveConnectionId((prev) => resolveActiveConnectionId(connections, prev));
  }, [connections]);

  // Active/last-active speakers first, others after. Derived here so the
  // component layer stays presentational and ranking rules have one home.
  const sortedRemotes = React.useMemo<TRTCConnection[]>(
    () => (remoteConnections ? Object.values(remoteConnections).sort((a, b) => speakerRank(a) - speakerRank(b)) : []),
    [remoteConnections]
  );

  // `true` when any remote is currently the active speaker — used by the pill
  // to pulse without re-mounting this hook in the collapsed view.
  const isAnyActiveSpeaker = React.useMemo(
    () => sortedRemotes.some((remote) => Boolean(remote.isActiveSpeaker)),
    [sortedRemotes]
  );

  // Phone (≤629px) visibility fallback: exactly ONE tile is shown. Priority:
  // active-speaker remote → first remote with camera on → self if camera on → none.
  // Consumed by the `ex3d-video--phone-hidden` class on each tile.
  const isSelfVideoEnabled = !!myCameraStream().get('isVideoEnabled');
  const phoneDisplayedKey = React.useMemo<string | null>(() => {
    const activeRemote = sortedRemotes.find((r) => r.isActiveSpeaker);
    if (activeRemote) return `remote:${activeRemote.connectionId}`;
    const cameraOnRemote = sortedRemotes.find((r) => !r.videoMuted && r.stream instanceof MediaStream);
    if (cameraOnRemote) return `remote:${cameraOnRemote.connectionId}`;
    if (isSelfVideoEnabled) return 'self';
    return null;
  }, [sortedRemotes, isSelfVideoEnabled]);

  return {
    remoteConnections,
    activeConnectionId,
    sortedRemotes,
    isAnyActiveSpeaker,
    phoneDisplayedKey,
    // AVS-4302 (§5.5): the standby dealer's own loopback, rendered in the "You" tile
    // instead of `myCameraStream` (which a standby never has). `undefined` for a master,
    // a customer, a flag-off tab, or before the loopback arrives — the self tile then
    // falls back to `myCameraStream` exactly as today.
    selfMirrorRemote,
    canShowVoip: tenantConfig().isAllowed('voip') && isVoipEnabled(),
  };
};

export default useVoipConnections;
